import * as React from 'react';
import classNames from 'classnames';
import { INumberOfFilesLinkProps } from '../../FileUploader.types';
import {
  nameSpace,
  NumberOfFilesTranslationPlaceholder,
  testIds,
  TRANSLATION_FALLBACKS,
  TRANSLATION_KEYS,
} from '../../constants';
import { usePopper } from '../../../../providers/usePopper/usePopper';
import { useClickOutside } from '../../../../providers/useClickOutside/useClickOutside';
import { FilesPopperContent } from '../FilesPopperContent/FilesPopperContent';
import style from './style/NumberOfFilesLink.scss';

export const NumberOfFilesLink: React.FunctionComponent<INumberOfFilesLinkProps> = ({
  value: files,
  placeholderLabel,
  translate,
  disabled,
  onFileRemoval,
  onXIconKeyDown,
  withErrorIndication,
  withErrorTooltip,
  validationMessage,
  id,
  uploadStatus,
  externallyOpenPopper,
  externallyClosePopper,
}) => {
  const isFixedPositionPopper = !!externallyOpenPopper;
  const hasFiles = !!files.length;
  const isPlaceholder = !hasFiles;
  const placeholder = placeholderLabel || ' ';
  const [isOpen, setOpen] = React.useState(false);

  const hideTooltip = (e?: React.MouseEvent<HTMLDivElement>) => {
    e?.stopPropagation();

    setOpen(false);
    externallyClosePopper?.();
  };
  const showTooltip = React.useCallback(
    (e?: React.MouseEvent<HTMLInputElement>) => {
      e?.stopPropagation();

      setOpen(true);
      externallyOpenPopper?.();
    },
    [externallyOpenPopper],
  );

  React.useEffect(() => {
    if (uploadStatus === 'Failed') {
      showTooltip();
    }
  }, [uploadStatus, showTooltip]);

  const {
    setRef: setPopperSourceElem,
    popper,
    setPopper: setPopperTargetElem,
    styles,
    attributes,
  } = usePopper<HTMLInputElement>({
    placement: 'bottom-start',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
      {
        name: 'flip',
        options: {
          boundary: 'element',
          fallbackPlacements: ['top-start', 'bottom-start'],
          allowedAutoPlacements: ['top-start', 'bottom-start'],
          offset: [0, 0],
        },
      },
      {
        name: 'offset',
        options: {
          offset: [10, 20],
        },
      },
    ],
  });

  useClickOutside([popper], () => hideTooltip());

  const numberOfFilesLinkClassName = classNames(style.numberOfFilesLink, {
    [style.hasFiles]: hasFiles,
    [style.placeholder]: isPlaceholder,
  });

  const getNumberOfSelectedFilesText = () => {
    const isSingleFileSelected = files.length === 1;
    const translatedText = isSingleFileSelected
      ? translate(
          nameSpace,
          TRANSLATION_KEYS['FileUploader.SingleSelectedFile'],
          TRANSLATION_FALLBACKS['FileUploader.SingleSelectedFile'],
        )
      : translate(
          nameSpace,
          TRANSLATION_KEYS['FileUploader.NumberOfSelectedFiles'],
          TRANSLATION_FALLBACKS['FileUploader.NumberOfSelectedFiles'],
        );

    return isSingleFileSelected
      ? translatedText
      : translatedText.replace(
          NumberOfFilesTranslationPlaceholder,
          `${files.length}`,
        );
  };

  const handleClick: React.MouseEventHandler<HTMLDivElement> = e => {
    if (isPlaceholder) {
      return;
    }

    e.preventDefault();
    showTooltip();
  };

  const handleFileRemoval: React.MouseEventHandler<SVGSVGElement> = e => {
    e.stopPropagation();
    if (files.length === 1) {
      hideTooltip();
    }

    onFileRemoval(e);
  };

  return (
    <div
      className={numberOfFilesLinkClassName}
      data-testid={testIds.numberOfFilesLink}
      onClick={handleClick}
      ref={setPopperSourceElem as React.LegacyRef<HTMLDivElement>}
    >
      {isPlaceholder ? placeholder : getNumberOfSelectedFilesText()}
      {isOpen && (
        <FilesPopperContent
          popperRef={setPopperTargetElem}
          id={id}
          popperStyles={styles.popper}
          popperAttributes={attributes.popper}
          closePopper={hideTooltip}
          files={files}
          disabled={disabled}
          withErrorIndication={withErrorIndication}
          withErrorTooltip={withErrorTooltip}
          validationMessage={validationMessage}
          onFileRemoval={handleFileRemoval}
          onXIconKeyDown={onXIconKeyDown}
          uploadStatus={uploadStatus}
          isFixedPositionPopper={isFixedPositionPopper}
          translate={translate}
        />
      )}
    </div>
  );
};
